import { ChevronRight } from 'lucide-react'
import type { PipelineCoupleList } from '@lib/constants/pipeline'
import { lookAtRows, type PipelineListRow } from '@lib/helpers/pipeline-text'
import type { VenuePipelineDto } from '@lib/dto/pipeline.dto'
import { Card } from '@/components/ui/card'
import { formatCount } from '@/lib/adFormat'

type LookAt = NonNullable<VenuePipelineDto['lookAt']>

function RowContent({ row, opens }: { row: PipelineListRow; opens: boolean }) {
  return (
    <>
      <span className="w-10 shrink-0 font-mono text-stat-sm font-semibold tabular-nums text-ink">{formatCount(row.count)}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-ink">{row.title}</span>
        <span className="block text-small text-ink-muted">{row.detail}</span>
      </span>
      {opens ? <ChevronRight aria-hidden className="h-4 w-4 shrink-0 text-ink-muted" /> : null}
    </>
  )
}

/**
 * Couples to look at (docs/pipeline-view.spec.md): who is waiting on the team
 * right now, whatever weeks are shown, each list opening its couples for those
 * who see the events.
 */
export function LookAtPanel({ lookAt, onList }: { lookAt: LookAt; onList?: ((list: PipelineCoupleList) => void) | undefined }) {
  const rows = lookAtRows(lookAt)
  return (
    <Card asChild>
      <section aria-labelledby="look-heading" className="flex min-w-0 flex-[1_1_20rem] flex-col gap-1 p-5">
        <h2 id="look-heading" className="m-0 mb-2 text-heading font-semibold text-ink">
          Couples to look at
        </h2>
        {rows.length === 0 ? <p className="m-0 text-sm text-ink-muted">No one is waiting on the team right now.</p> : null}
        <ul className="m-0 list-none p-0">
          {rows.map((row) => (
            <li key={row.list} className="border-t border-line">
              {onList === undefined ? (
                <div className="flex items-center gap-4 py-3">
                  <RowContent row={row} opens={false} />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => onList(row.list)}
                  className="flex w-full items-center gap-4 rounded-sm py-3 text-left hover:bg-row-hover focus-visible:focus-ds"
                >
                  <RowContent row={row} opens />
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>
    </Card>
  )
}
